import { useTranslate } from '@ur-solutions/react-hooks'
import { useQuery } from '@apollo/client/react/hooks/useQuery'
import { useMutation } from '@apollo/client/react/hooks/useMutation'
import { usePrompt, useToast } from '@ur-solutions/react-components'

import { PermissionGroupSection } from '../PermissionGroupSection'
import { ALL_COMPANY_VEHICLE_GROUPS_QUERY } from '@/modules/companies/queries'
import {
  CREATE_VEHICLE_GROUP,
  DELETE_VEHICLE_GROUP,
  PATCH_VEHICLE_GROUP,
} from '@/modules/companies/mutations'
import CreateOrEditVehicleGroupModal from './CreateOrEditVehicleGroupModal'
import { type VehicleGroupForm } from '../../types'
import { type CompanyVehicleGroupNode, OldAddButton } from '@/modules/companies'
import { useMemo } from 'react'
import { usePermissions } from '@/utils/hooks/usePermissions'
import { PERMISSIONS } from '@/utils/permissions'

const VehicleGroupSettings = () => {
  const translations = useTranslate({
    vehicleGroups: 'companies.vehicle-groups',
    vehicleGroupsDescription: 'companies.vehicle-groups-description',
    toast: {
      successCreate: 'companies.toasts.vehicle-group-create-success',
      successUpdate: 'companies.toasts.vehicle-group-update-success',
      successDelete: 'companies.toasts.vehicle-group-delete-success',
    },
  })

  const prompt = usePrompt()
  const toast = useToast()

  const { hasPermissions } = usePermissions()
  const canAdd = hasPermissions(PERMISSIONS.companies.add.vehicleGroup, true)
  const canEdit = hasPermissions(
    PERMISSIONS.companies.change.vehicleGroup,
    true
  )
  const canDelete = hasPermissions(
    PERMISSIONS.companies.delete.vehicleGroup,
    true
  )

  const { data, loading } = useQuery(ALL_COMPANY_VEHICLE_GROUPS_QUERY)
  const vehicleGroupsRefetchQueries = useMemo(
    () => [
      {
        query: ALL_COMPANY_VEHICLE_GROUPS_QUERY,
      },
    ],
    []
  )

  const [createVehicleGroup] = useMutation(CREATE_VEHICLE_GROUP, {
    refetchQueries: vehicleGroupsRefetchQueries,
    onCompleted: () => {
      toast('success', translations.toast.successCreate)
    },
  })

  const [updateVehicleGroup] = useMutation(PATCH_VEHICLE_GROUP, {
    refetchQueries: vehicleGroupsRefetchQueries,
    onCompleted: () => {
      toast('success', translations.toast.successUpdate)
    },
  })

  const [deleteVehicleGroup] = useMutation(DELETE_VEHICLE_GROUP, {
    refetchQueries: vehicleGroupsRefetchQueries,
    onCompleted: () => {
      toast('success', translations.toast.successDelete)
    },
  })

  const vehicleGroups = data?.allCompanyVehicleGroups ?? []

  const handleCreateVehicleGroup = async () => {
    const { data } = await prompt<VehicleGroupForm | null>(resolve => (
      <CreateOrEditVehicleGroupModal onSubmit={resolve} />
    ))

    if (!data) return // User cancelled

    await createVehicleGroup({
      variables: {
        input: {
          name: data.name,
          users: data.users,
          vehicles: data.vehicles.map(({ id }) => id),
        },
      },
    })
  }

  const handleEditVehicleGroup = async (
    vehicleGroup: CompanyVehicleGroupNode
  ) => {
    const initialValues = {
      name: vehicleGroup.name,
      users: vehicleGroup.users.map(user => user.id),
      vehicles: vehicleGroup.vehicles.map(vehicle => ({
        id: vehicle.id,
        name: vehicle.registrationNumber || vehicle.vin,
      })),
    }

    const { data } = await prompt<VehicleGroupForm | null>(resolve => (
      <CreateOrEditVehicleGroupModal
        initialValues={initialValues}
        onSubmit={resolve}
      />
    ))

    if (!data) return

    void updateVehicleGroup({
      variables: {
        id: vehicleGroup.id,
        input: {
          name: data.name,
          users: data.users,
          vehicles: data.vehicles.map(({ id }) => id),
        },
      },
    })
  }

  const handleDeleteVehicleGroup = (vehicleGroup: CompanyVehicleGroupNode) => {
    void deleteVehicleGroup({
      variables: {
        id: vehicleGroup.id,
      },
    })
  }

  return (
    <PermissionGroupSection
      header={{
        title: translations.vehicleGroups,
        description: translations.vehicleGroupsDescription,
        loading,
        children: (
          <>
            {!loading && canAdd && (
              <OldAddButton adding={false} onClick={handleCreateVehicleGroup} />
            )}
          </>
        ),
      }}
      loading={loading}
      table={{
        rows: vehicleGroups,
        handleEdit: handleEditVehicleGroup,
        handleDelete: handleDeleteVehicleGroup,
      }}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  )
}

export default VehicleGroupSettings
